import { ScreenContainer } from 'src/components/flow';
import { getTotalDebt } from './utils';
import type { FlowScreenProps } from '../types';
import { useDebtStateHandler } from './custom-hooks/useDebtStateHandler';
import { DebtDialog } from './debt-dialog';
import { DebtInput } from './debt-input';
import { ConfirmedDebtInput } from './confirmed-debt-input';
import { Typography, Button, Box } from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  stringifyAnswerValue,
  updateQuizAnswerAnalytics,
} from 'src/utils/analytics/utils';
import { Input } from 'src/lib/constants';
import {
  CurrentBalanceCompany,
  DebtTypeEnum,
  FullDebt,
} from 'src/types/inputs';
import { inputType, SafeInputType } from './types';
import { getInputLabel } from 'src/utils/inputs/labels';
import { useCallback, useEffect } from 'react';
import { InfomarkerIcon } from 'src/components/screens/shared/infomarker-icon';
import { useSegmentPageAction } from 'src/utils/analytics/hooks';
import { PageActionName, PageActionType } from 'src/utils/analytics/constants';

const sanitizeInputs = (
  innerStateInputs: Record<string, inputType>
): FullDebt[] => {
  const confirmedDebts = Object.values(innerStateInputs).filter(
    (debt): debt is SafeInputType =>
      debt.confirmed === true && debt.amount !== null
  );

  return confirmedDebts.map((confirmedDebt) => ({
    debtType: confirmedDebt.debt_type as DebtTypeEnum,
    institution: confirmedDebt.institution as CurrentBalanceCompany,
    amount: Number(confirmedDebt.amount),
  }));
};

const AddButton = ({
  onClick,
  buttonText,
}: {
  onClick: () => void;
  buttonText: string;
}) => (
  <Box width={{ xs: '100%', sm: 'fit-content' }} marginTop={3} minWidth={150}>
    <Button variant="outlined" fullWidth onClick={onClick}>
      <Typography variant="accent" sx={{ textTransform: 'uppercase' }}>
        {buttonText}
      </Typography>
    </Button>
  </Box>
);

const TotalDebt = ({ totalDebt }: { totalDebt: string }) => {
  if (totalDebt === '$0') return null;
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      marginTop={5}
      marginBottom={{ xs: '1.75rem', md: '-1.75rem' }}
      height="44px"
      paddingX={2}
      maxWidth={{ xs: '100%', md: '300px' }}
    >
      <Typography variant="bodyBlack">Total Debt:</Typography>
      <Typography>{totalDebt}</Typography>
    </Box>
  );
};

const HelperText = () => (
  <Box
    display="flex"
    flexDirection="row"
    justifyContent="space-evenly"
    alignItems="center"
    marginTop={1}
  >
    <WarningAmberIcon
      sx={{ width: '11px', height: '11px', color: 'red.main' }}
    />
    <Typography variant="bodySmall" textAlign="left" color="red.main">
      {`Please confirm your debt or it won't be saved.`}
    </Typography>
  </Box>
);

const AddDebtScreen: React.FC<FlowScreenProps> = ({
  inputs,
  updateInput,
  pageNumber,
  trackEvent,
  nerdyTip = '',
}) => {
  const [
    inputState,
    handleOnClick,
    handleOnChange,
    confirmDebt,
    unConfirmDebt,
    removeDebt,
    closeDialog,
    unavailableInputs,
  ] = useDebtStateHandler(inputs[Input.FullDebt]);
  const trackPageAction = useSegmentPageAction();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  const totalDebt = getTotalDebt(inputState);

  const firstUnconfirmedDebt = Object.values(inputState).find(
    (debt) => !debt.confirmed
  );

  const firstConfirmedDebt = Object.values(inputState).find(
    (debt) => debt.confirmed
  );

  const handleDebtUpdate = useCallback(
    (
      idx: string,
      action: typeof confirmDebt | typeof unConfirmDebt | typeof removeDebt
    ) => {
      action(idx);
    },
    []
  );

  useEffect(() => {
    updateInput({ name: Input.FullDebt, value: sanitizeInputs(inputState) });
  }, [updateInput, inputState]);

  return (
    <ScreenContainer
      nerdyTip={nerdyTip}
      primaryCta={{
        text: 'next',
        isDisabled: firstConfirmedDebt === undefined,
        onClick: async () => {
          const { eventType, payload } = updateQuizAnswerAnalytics(
            inputs[Input.FullDebt],
            Input.FullDebt,
            pageNumber
          );
          trackPageAction({
            actionName: PageActionName.FullDebt,
            actionType: PageActionType.Select,
            actionInput: stringifyAnswerValue(inputs[Input.FullDebt]),
          });
          trackEvent(eventType, payload);
        },
      }}
      helperText={
        matches &&
        firstConfirmedDebt !== undefined &&
        firstUnconfirmedDebt !== undefined ? (
          <HelperText />
        ) : undefined
      }
      additionalContentTop={<TotalDebt totalDebt={totalDebt} />}
    >
      {' '}
      <>
        <Typography variant="titleSmall" component="span" marginBottom={2}>
          {getInputLabel(Input.FullDebt, inputs)}
        </Typography>
        {nerdyTip && <InfomarkerIcon />}
      </>
      <Box display="flex" flexDirection="column">
        {Object.values(inputState).map((debt) => {
          if (debt.confirmed)
            return (
              <ConfirmedDebtInput
                key={debt.index}
                debt={debt}
                onRemove={(idx) => handleDebtUpdate(idx, removeDebt)}
                onEdit={(idx) => handleDebtUpdate(idx, unConfirmDebt)}
              />
            );
        })}
        {!matches && firstUnconfirmedDebt && (
          <DebtDialog
            isOpen={firstUnconfirmedDebt.isOpen!}
            debt={inputState[firstUnconfirmedDebt.index!]}
            onChange={handleOnChange}
            onConfirm={(idx) => handleDebtUpdate(idx, confirmDebt)}
            onCancel={() => closeDialog(firstUnconfirmedDebt.index)}
            unavailableInputs={unavailableInputs}
          />
        )}
        {matches &&
          Object.values(inputState).map((debt) => {
            if (!debt.confirmed)
              return (
                <DebtInput
                  key={debt.index}
                  debt={debt}
                  onConfirm={(idx) => handleDebtUpdate(idx, confirmDebt)}
                  onRemove={(idx) => handleDebtUpdate(idx, removeDebt)}
                  onChange={handleOnChange}
                  unavailableInputs={unavailableInputs}
                />
              );
          })}
      </Box>
      <AddButton
        onClick={() => handleOnClick(matches)}
        buttonText={
          firstConfirmedDebt !== undefined ? '+ add another debt' : '+ add debt'
        }
      />
      <Box
        display={{
          xs: 'none',
          sm: 'block',
        }}
      >
        <TotalDebt totalDebt={totalDebt} />
      </Box>{' '}
    </ScreenContainer>
  );
};

export default AddDebtScreen;
